import { FC, useState } from 'react';

import { Box, Button, ButtonBase, CircularProgress, Collapse, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered';
import SortIcon from '@material-ui/icons/Sort';

import type { ExerciseQueue } from 'api/rest/exerciseQueues/exerciseQueues.types';
import type { Section } from 'api/rest/sections/sections.types';
import ChipWithNumber from 'components/ChipWithNumber/ChipWithNumber';

import OnGoLayout from '../OnGoLayout';
import FormErrorBanner from '../Initialize/FormErrorBanner';
import type { OnGoInitializeFormValues } from '../Initialize/onGoInitialize.types';
import { resolvePluralLabel } from '../Initialize/resolvePluralLabel';
import { formatQueueLengthNoun, resolveQueuePreviewDetails, type QueuePreviewScopeEntry } from './resolveQueuePreviewDetails';

const useStyles = makeStyles(theme => ({
  intro: {
    marginBottom: theme.spacing(2.5),
  },
  introTitle: {
    fontWeight: 700,
    lineHeight: 1.2,
    marginBottom: theme.spacing(1.5),
  },
  introCopy: {
    color: theme.palette.text.secondary,
  },
  summaryChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(3),
  },
  section: {
    paddingTop: theme.spacing(3.5),
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '0.75rem',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  scopeGroups: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  scopeGroup: {
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  scopeGroupHeader: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 2.5),
    textAlign: 'left',
  },
  scopeGroupTitle: {
    fontWeight: 600,
    minWidth: 0,
  },
  scopeGroupHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    flexShrink: 0,
    color: theme.palette.text.secondary,
  },
  scopeGroupHeaderMeta: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(0.25),
  },
  scopeAllBadge: {
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: 0.2,
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
  },
  subsectionCount: {
    fontSize: '0.65rem',
    lineHeight: 1.2,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  subsectionChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0, 2.5, 2),
    borderTop: `1px solid ${theme.palette.grey[200]}`,
    paddingTop: theme.spacing(1.5),
  },
  emptyScope: {
    color: theme.palette.text.secondary,
  },
  footer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(1.5),
  },
}));

const formatSubsectionCount = (count: number): string => `${count} ${resolvePluralLabel(count, 'subsection')}`;

type ScopePreviewGroupProps = {
  entry: QueuePreviewScopeEntry;
  classes: ReturnType<typeof useStyles>;
};

const ScopePreviewGroup: FC<ScopePreviewGroupProps> = ({ entry, classes }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const subsectionCountLabel = formatSubsectionCount(entry.subsectionCount);

  return (
    <Box className={classes.scopeGroup}>
      <ButtonBase
        aria-expanded={isExpanded}
        className={classes.scopeGroupHeader}
        component='div'
        onClick={() => setIsExpanded(prev => !prev)}
      >
        <Typography className={classes.scopeGroupTitle} variant='body2'>
          {entry.sectionName}
        </Typography>
        <Box className={classes.scopeGroupHeaderRight}>
          <Box className={classes.scopeGroupHeaderMeta}>
            <Typography className={classes.subsectionCount} component='span'>
              {subsectionCountLabel}
            </Typography>
            {entry.allSubsectionsSelected ? (
              <Typography className={classes.scopeAllBadge} component='span'>
                Cały dział
              </Typography>
            ) : null}
          </Box>
          {isExpanded ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />}
        </Box>
      </ButtonBase>

      <Collapse in={isExpanded} mountOnEnter unmountOnExit>
        <Box className={classes.subsectionChips}>
          {entry.subsectionNames.map(name => (
            <ChipWithNumber key={name} label='' size='small' value={name} variant='outlined' />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export type OnGoQueuePreviewProps = {
  formValues: OnGoInitializeFormValues;
  queue: ExerciseQueue;
  sectionsData: Section[];
  isChanging: boolean;
  isStarting: boolean;
  errorMessage: string | null;
  onChangeSettings: () => void;
  onStart: () => void;
};

const OnGoQueuePreview: FC<OnGoQueuePreviewProps> = ({
  formValues,
  queue,
  sectionsData,
  isChanging,
  isStarting,
  errorMessage,
  onChangeSettings,
  onStart,
}) => {
  const classes = useStyles();
  const details = resolveQueuePreviewDetails(formValues, sectionsData, queue);
  const isBusy = isChanging || isStarting;

  return (
    <OnGoLayout
      footer={
        <Box className={classes.footer}>
          <Button disabled={isBusy} fullWidth onClick={onChangeSettings} size='large' variant='outlined'>
            {isChanging ? <CircularProgress size={18} /> : 'Zmień'}
          </Button>
          <Button
            color='primary'
            disabled={isBusy}
            endIcon={isStarting ? <CircularProgress color='inherit' size={18} /> : undefined}
            fullWidth
            onClick={onStart}
            size='large'
            variant='contained'
          >
            {isStarting ? 'Startujemy…' : 'Zaczynamy'}
          </Button>
        </Box>
      }
    >
      <Box className={classes.intro}>
        <Typography className={classes.introTitle} component='h1' variant='h5'>
          Twoja kolejka jest gotowa
        </Typography>
        <Typography className={classes.introCopy} variant='body2'>
          Jeśli wszystko się zgadza, rozpocznij ćwiczenia.
        </Typography>
      </Box>

      <Box className={classes.summaryChips}>
        <ChipWithNumber label={formatQueueLengthNoun(details.queueLength)} size='medium' value={details.queueLength} />
        <ChipWithNumber label='' size='medium' value={details.sortLabel} />
        {details.exerciseTypeLabels.map(label => (
          <ChipWithNumber key={label} label='' size='medium' value={label} />
        ))}
      </Box>

      {errorMessage ? (
        <Box mb={2}>
          <FormErrorBanner message={errorMessage} />
        </Box>
      ) : null}

      <Box className={classes.section} component='section'>
        <Typography className={classes.sectionTitle} component='h2'>
          Zakres programu
        </Typography>
        {details.allSectionsSelected ? (
          <ChipWithNumber icon={<DoneAllIcon />} label='' value='Wszystkie działy' />
        ) : details.scopeEntries.length > 0 ? (
          <Box className={classes.scopeGroups}>
            {details.scopeEntries.map(entry => (
              <ScopePreviewGroup key={entry.sectionName} classes={classes} entry={entry} />
            ))}
          </Box>
        ) : (
          <Typography className={classes.emptyScope} variant='body2'>
            Brak wybranego zakresu.
          </Typography>
        )}
      </Box>
    </OnGoLayout>
  );
};

export default OnGoQueuePreview;
